import classNames from 'classnames';
import Cleave from 'cleave.js/react';
import { isNil } from 'lodash';
import React, { Component } from 'react';
import './BaseInput.scss';
import If from '../If/If';
import { coalesce } from '../../../utils/utils';
import { isAddBank, isQCD } from '../../../utils/transactions-util';

class BaseInput extends Component {
  constructor(props) {
    super(props);
    this.state = {
      baseInputCleave: null,
      focused: props.autoFocus ? true : false,
      rawValue: '',
    };
    this.baseInputInit = this.baseInputInit.bind(this);
    this.blurHandler = this.blurHandler.bind(this);
    this.changeHandler = this.changeHandler.bind(this);
    this.focusHandler = this.focusHandler.bind(this);
    this.setCleaveRawValue = this.setCleaveRawValue.bind(this);
    this.inputRef = React.createRef();
  }

  componentDidUpdate(prevProps) {
    // Force update Cleave.js rawValue to defaultValue being passed in since Cleave
    // maintains its own state and does not automatically update when a new value is
    // passed into the input.
    const { inputRef } = this;
    const { baseInputCleave, focused } = this.state;
    const { defaultValue, disabled, setFocus } = this.props;

    if (prevProps.setFocus !== setFocus && setFocus && !focused) {
      inputRef.current.element.focus();
      this.setState({ focused: true });
    }

    // disabling a control while it has focus does not trigger onBlur
    if (focused && disabled) {
      this.setState({ focused: false });
    }

    if (
      isNil(baseInputCleave) ||
      (focused && !disabled) ||
      defaultValue === prevProps.defaultValue
    ) {
      return;
    }
    this.setCleaveRawValue(defaultValue);
  }
  baseInputInit(cleave) {
    if (!isNil(cleave)) {
      this.setState({
        baseInputCleave: cleave,
      });
    }
  }
  blurHandler(event) {
    const { onBlur, onBlurFormat } = this.props;
    this.setState({ focused: false });

    if (onBlurFormat) {
      const { rawValue, value } = event.target;
      const appliedValue = coalesce(rawValue, value);

      const appliedValue2 = onBlurFormat(appliedValue);
      this.setState({ rawValue: appliedValue2 });
    }
    if (onBlur) {
      onBlur(event);
    }
  }
  changeHandler(event) {
    const { onChange } = this.props;
    if (onChange) {
      const { rawValue, value } = event.target;
      const appliedValue = coalesce(rawValue, value);
      this.setState({ rawValue: appliedValue });
      onChange(appliedValue, event);
    }
  }
  focusHandler(event) {
    const { onFocus } = this.props;
    const { baseInputCleave } = this.state;

    if (baseInputCleave) {
      this.setState({
        focused: true,
        rawValue: baseInputCleave.getRawValue(),
      });
    }
    if (onFocus) {
      onFocus(event);
    }
  }
  setCleaveRawValue(value) {
    this.state.baseInputCleave.setRawValue(value ? value : '');
  }
  render() {
    const {
      ariaLabel,
      autoComplete = 'off',
      autoFocus,
      cleaveOptions,
      containerClassName = '',
      defaultValue,
      disabled,
      displayInline,
      displayValidationMessage = true,
      errorClassName = '',
      error,
      inputClassName = '',
      isRelative,
      role = 'textbox',
      displayErrorMessage,
      tabIndex = '0',
      validationMessage,
      wrapperClassName = '',
      ...rest
    } = this.props;
    const { focused, rawValue } = this.state;
    const inputProps = {
      ...rest,
      'aria-label': ariaLabel, // JSX special case
      autoComplete,
      autoFocus,
      className: `${inputClassName} base-input-field`,
      disabled: disabled && 'disabled',
      onChange: this.changeHandler,
      onBlur: this.blurHandler,
      onFocus: this.focusHandler,
      ref: this.inputRef,
      role: role,
      tabIndex: tabIndex,
      value: rawValue ? rawValue : coalesce(defaultValue, ''),
    };
    const inputWrapperStyles = classNames(
      isAddBank() ? 'base-input-wrapper_plaid' : 'base-input-wrapper',
      {
        'base-input-focused': focused,
        'base-input-error': error,
        'base-input-disabled': disabled,
      }
    );
    const onInit = this.baseInputInit;

    return (
      <div
        className={`${containerClassName} base-input-container ${
          displayInline ? 'base-input-inline' : ''
        }`}
      >
        <div className={`${inputWrapperStyles} ${wrapperClassName}`}>
          {cleaveOptions ? (
            <Cleave
              onInit={onInit}
              htmlRef={(ref) => (inputProps.ref = ref)}
              options={cleaveOptions}
              {...inputProps}
            />
          ) : (
            <input {...inputProps} />
          )}
        </div>
        <If
          test={
            displayValidationMessage &&
            validationMessage &&
            !displayErrorMessage &&
            !isQCD()
          }
        >
          <div className="base-input-validation-message">
            {validationMessage}
          </div>
        </If>
        <If test={error && displayErrorMessage}>
          <div
            className={`base-input-errorMessage ${
              isRelative ? 'base-input-relative' : ''
            } ${errorClassName}`}
          >
            {error}
          </div>
        </If>
      </div>
    );
  }
}

export default BaseInput;
